import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Head from 'next/head';
import Link from 'next/link';
import { useRouter } from 'next/router';
import HomeHeader from '../homepage/HomeHeader';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import { COMMUNITY_VIDEO_COMPOSE_PATH } from '../../lib/auth/returnDestination';
import PublicAvatar from './PublicAvatar';
import FlyerPlacement from '../flyers/FlyerPlacement';
import { GroceryTopicInspiration } from './GroceryTopicInspiration';
import { GROCERY_PROMPT_CATEGORIES, isRecognizedInspirationQuestion } from '../../lib/community/inspiration';
import { getCommunityFoldPreview } from './communityPostPreview';
import { isCommercialPost, FLYER_BUILDER_PATH } from '../../lib/community/commercialPostGuard';
import GoatChat from './GoatChat';
import CommunityPostVideoViewer from './CommunityPostVideoViewer';
import CommunityFlyerNudge from '../flyers/CommunityFlyerNudge';
import CommunityVideoFeed from './CommunityVideoFeed';
import {
  addComment,
  checkLikeStatus,
  createPost,
  fetchPost,
  fetchPosts,
  getGuestLikeStatuses,
  getPostAuthorDisplay,
  getVideoPreviewUrl,
  toggleGuestLike,
  toggleLike,
  uploadPostMedia,
  type Post,
} from '../../utils/communityUtils';

import { readFollowedConversationIds, toggleFollowedConversation } from '../../lib/community/following';

type CommunityView = 'hot' | 'near' | 'following' | 'spaces' | 'videos';
type PrimaryCommunityView = 'videos' | 'conversations';
const PRIMARY_VIEW_STORAGE_KEY = 'youdle-community-primary-view-v1';
type FilterKey = 'all' | 'iso' | 'shelf' | 'deals' | 'tips' | 'alerts';
type SortKey = 'helpful' | 'newest';

const PAGE_SIZE = 10;

const ZIP_STORAGE_KEY = 'youdle-community-v5-zipcode';
const MAX_MEDIA = 4;

const categories: Array<{ id: FilterKey; label: string; backend?: string }> = [
  { id: 'all', label: 'All Conversations' },
  { id: 'iso', label: 'In Search Of', backend: 'ISO Requests' },
  { id: 'shelf', label: 'Shelf sightings', backend: 'Stock Updates' },
  { id: 'deals', label: 'Deals & prices', backend: 'Deals & Discounts' },
  { id: 'tips', label: 'Tips & advice', backend: 'Reviews & Recommendations' },
  { id: 'alerts', label: 'Alerts & safety' },
];

const createCategories = [
  { label: 'Ask a question', value: 'ISO Requests' },
  { label: 'Share a Shelfie or availability', value: 'Stock Updates' },
  { label: 'Share a deal or price', value: 'Deals & Discounts' },
  { label: 'Share a grocery tip or review', value: 'Reviews & Recommendations' },
  { label: 'Food assistance and donation', value: 'Food Donation and Distribution' },
  { label: 'Food recall or safety alert', value: 'Food Safety & Recalls' },
  { label: 'General conversation', value: 'General' },
];

const spaceConcepts = [
  {
    name: 'Memphis Grocery Finds',
    icon: '🥬',
    summary: 'Local store finds, affordable staples, price checks, and seasonal grocery needs.',
    tag: 'Local grocery Space idea',
    search: 'Memphis',
  },
  {
    name: 'Baby Feeding & Formula',
    icon: '🍼',
    summary: 'Support for caregivers seeking exact products, sizes, and current availability.',
    tag: 'Topic Space idea',
    search: 'formula',
  },
];

function postDate(input: string): string {
  const date = new Date(input);
  if (Number.isNaN(date.getTime())) return '';
  const elapsed = Date.now() - date.getTime();
  if (elapsed >= 0 && elapsed < 60 * 60 * 1000) {
    return Math.max(1, Math.round(elapsed / 60000)) + 'm ago';
  }
  if (elapsed >= 0 && elapsed < 24 * 60 * 60 * 1000) return Math.round(elapsed / 3600000) + 'h ago';
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function tagForCategory(category: string) {
  const match: Record<string, { name: string; style: string }> = {
    'ISO Requests': { name: 'IN SEARCH OF', style: 'sky' },
    'Stock Updates': { name: 'SHELF SIGHTING', style: '' },
    'Deals & Discounts': { name: 'DEAL & PRICE', style: 'amber' },
    'Reviews & Recommendations': { name: 'TIPS & ADVICE', style: '' },
    'Food Donation and Distribution': { name: 'FOOD HELP', style: 'sky' },
  };
  return match[category] ?? { name: category.toUpperCase(), style: 'gray' };
}

function isAlert(post: Post): boolean {
  const content = [post.title ?? '', post.content ?? '', post.category ?? ''].join(' ');
  return /\b(recall|alert|safety|contaminat|outbreak|shortage|warning)\b/i.test(content);
}

export default function CommunityV5() {
  const router = useRouter();
  const { isAuthenticated, loadingUser, userId, zipcode: accountZipcode } = useAuth();
  // First-time visitors see the videos without registering. Text is always one tap away.
  const [view, setView] = useState<CommunityView>('videos');
  const [category, setCategory] = useState<FilterKey>('all');
  const [sort, setSort] = useState<SortKey>('newest');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [zip, setZip] = useState('');
  const [posts, setPosts] = useState<Post[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [videoPosts, setVideoPosts] = useState<Post[]>([]);
  const [videoTotal, setVideoTotal] = useState(0);
  const [videoLoading, setVideoLoading] = useState(false);
  const [videoFetched, setVideoFetched] = useState(false);
  const [videoPage, setVideoPage] = useState(0);
  const [videoHasMore, setVideoHasMore] = useState(true);
  const [videoLoadError, setVideoLoadError] = useState(false);
  const videoRequestInFlight = useRef(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [liked, setLiked] = useState<Record<string, boolean>>({});
  const [busyLikes, setBusyLikes] = useState<Record<string, boolean>>({});
  const [followedIds, setFollowedIds] = useState<string[]>([]);
  const [expandedPostId, setExpandedPostId] = useState<string | null>(null);
  const [playingVideoPost, setPlayingVideoPost] = useState<Post | null>(null);
  const [nearbyRadius, setNearbyRadius] = useState(25);
  const [nearbyRadiusInput, setNearbyRadiusInput] = useState('25');
  const [composerOpen, setComposerOpen] = useState(false);
  const [composerCategory, setComposerCategory] = useState('ISO Requests');
  const [inspirationQuestion, setInspirationQuestion] = useState<string | null>(null);
  const [inspirationRefresh, setInspirationRefresh] = useState(0);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [anonymous, setAnonymous] = useState(false);
  const [media, setMedia] = useState<File[]>([]);
  const [videoComposer, setVideoComposer] = useState(false);
  const videoFileInputRef = useRef<HTMLInputElement>(null);
  const [submitting, setSubmitting] = useState(false);
  const [replyPost, setReplyPost] = useState<Post | null>(null);
  const [reply, setReply] = useState('');
  const [submittingReply, setSubmittingReply] = useState(false);
  const [refreshCount, setRefreshCount] = useState(0);
  const [goatChatOpen, setGoatChatOpen] = useState(false);

  useEffect(() => {
    try {
      setFollowedIds(readFollowedConversationIds(window.localStorage));
      const savedRadius = Number(window.localStorage.getItem('youdle-community-radius-miles'));
      if (Number.isInteger(savedRadius) && savedRadius >= 1 && savedRadius <= 50) {
        setNearbyRadius(savedRadius); setNearbyRadiusInput(String(savedRadius));
      }
      const savedZip = window.localStorage.getItem(ZIP_STORAGE_KEY) || '';
      if (/^\d{5}$/.test(savedZip)) setZip(savedZip);
    } catch { /* Guest browsing may block storage. */ }
  }, []);

  useEffect(() => {
    if (accountZipcode && /^\d{5}$/.test(accountZipcode) && !zip) setZip(accountZipcode);
  }, [accountZipcode, zip]);

  useEffect(() => {
    if (!router.isReady) return;
    const requested = router.query.category;
    const found = categories.find(c => c.backend === requested);
    if (found) setCategory(found.id);
    if (typeof router.query.search === 'string') setSearch(router.query.search.slice(0, 150));
    const requestedView = router.query.view;
    if (requestedView === 'videos') {
      setView('videos');
    } else if (requestedView === 'conversations' || requestedView === 'text' ||
      requestedView === 'hot' || requestedView === 'near' || requestedView === 'following') {
      setView(requestedView === 'near' || requestedView === 'following' ? requestedView : 'hot');
    } else if (router.query.compose === '1' || typeof router.query.search === 'string' ||
      typeof router.query.category === 'string') {
      setView('hot');
    } else {
      try {
        if (window.localStorage.getItem(PRIMARY_VIEW_STORAGE_KEY) === 'conversations') {
          setView('hot');
        }
      } catch { /* Visiting as a guest never requires browser storage. */ }
    }
  }, [router.isReady, router.query.category, router.query.search, router.query.view, router.query.compose]);

  useEffect(() => {
    const handle = window.setTimeout(() => setDebouncedSearch(search.trim()), 250);
    return () => window.clearTimeout(handle);
  }, [search]);

  useEffect(() => { setPage(1); }, [view, category, sort, debouncedSearch, zip, nearbyRadius]);
  // Only one conversation is expanded at a time, and changing feed views folds it.
  useEffect(() => { setExpandedPostId(null); }, [view, category, sort, debouncedSearch, zip, nearbyRadius, page]);

  const load = useCallback(async () => {
    if (view === 'videos') {
      setLoading(false); setError('');
      return;
    }
    if (view === 'spaces') {
      setLoading(false);
      setError('');
      return;
    }
    if (view === 'near' && !/^\d{5}$/.test(zip)) {
      setPosts([]);
      setTotal(0);
      setLoading(false);
      setError('');
      return;
    }
    setLoading(true);
    setError('');
    try {
      let fetched: Post[] = [];
      let count = 0;
      if (view === 'following') {
        const selected = followedIds.slice(0, 50);
        const results = await Promise.all(selected.map(id => fetchPost(id).catch(() => null)));
        const available = results.filter((post): post is Post => !!post);
        const matchesSearch = (post: Post) =>
          (post.title ?? '').toLowerCase().includes(debouncedSearch.toLowerCase()) ||
          post.content.toLowerCase().includes(debouncedSearch.toLowerCase());
        const expectedCategory = categories.find(item => item.id === category)?.backend;
        const belongsToFilter = (post: Post) => {
          if (category === 'all') return true;
          if (category === 'alerts') {
            return isAlert(post) || !!post.video_feed_filters?.includes('alerts');
          }
          if (category === 'deals' || category === 'tips') {
            return post.category === expectedCategory ||
              !!post.video_feed_filters?.includes(category);
          }
          return !!expectedCategory && post.category === expectedCategory;
        };
        const filtered = available.filter(post =>
          belongsToFilter(post) && (!debouncedSearch || matchesSearch(post)));
        count = filtered.length;
        fetched = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
      } else {
        const categoryToFetch = categories.find(c => c.id === category)?.backend;
        const filter = {
          category: categoryToFetch ?? 'All Posts',
          searchQuery: debouncedSearch,
          inMyArea: view === 'near',
          userZipcode: zip,
          radiusMiles: nearbyRadius,
          sort: sort === 'helpful' ? 'discussed' as const : 'latest' as const,
          feedFilter: category === 'deals' || category === 'tips' || category === 'alerts'
            ? category : undefined,
        };
        const response = await fetchPosts(filter, { page, pageSize: PAGE_SIZE });
        fetched = response.posts;
        count = response.total;
      }
      setPosts(fetched);
      setTotal(count);
      const results: Record<string, boolean> = {};
      if (fetched.length) {
        if (isAuthenticated && userId) {
          await Promise.all(fetched.map(async post => {
            const status = await checkLikeStatus(post.post_id, userId);
            results[post.post_id] = status.liked;
          }));
        } else if (!isAuthenticated && !loadingUser) {
          Object.assign(results, await getGuestLikeStatuses(fetched.map(p => p.post_id)));
        }
      }
      setLiked(results);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'We could not load Community conversations.');
    } finally {
      setLoading(false);
    }
  }, [view, followedIds, debouncedSearch, category, sort, page, zip, nearbyRadius, isAuthenticated, userId, loadingUser, refreshCount]);

  useEffect(() => {
    let active = true;
    void load().catch(() => {
      if (active) setError('Unable to load Community.');
    });
    return () => { active = false; };
  }, [load]);

  const loadVideos = useCallback(async (loadMore = false) => {
    if (videoRequestInFlight.current || (loadMore && !videoHasMore)) return;
    videoRequestInFlight.current = true;
    setVideoLoading(true);
    setVideoFetched(true);
    setVideoLoadError(false);
    const nextPage = loadMore ? videoPage + 1 : 1;
    try {
      const result = await fetchPosts({mediaType:'video'}, {page:nextPage,pageSize:10});
      setVideoPage(nextPage);
      setVideoTotal(result.total);
      setVideoHasMore(result.posts.length > 0 && nextPage * 10 < result.total);
      setVideoPosts(current => loadMore
        ? [...current, ...result.posts.filter(p => !current.some(existing => existing.post_id === p.post_id))]
        : result.posts);
      // Videos and text conversations share genuine likes.
      if (result.posts.length) {
        try {
          const statuses: Record<string, boolean> = {};
          if (isAuthenticated && userId) {
            await Promise.all(result.posts.map(async post => {
              statuses[post.post_id] = (await checkLikeStatus(post.post_id, userId)).liked;
            }));
          } else if (!isAuthenticated && !loadingUser) {
            Object.assign(statuses, await getGuestLikeStatuses(result.posts.map(post => post.post_id)));
          }
          setLiked(current => ({ ...current, ...statuses }));
        } catch { /* A like-status error must not stop video playback. */ }
      }
    } catch {
      setVideoLoadError(true);
      toast.error('Could not load more videos. Please try again.');
    } finally {
      videoRequestInFlight.current = false;
      setVideoLoading(false);
    }
  }, [videoPage, videoHasMore, isAuthenticated, userId, loadingUser]);
  useEffect(() => {
    if (view === 'videos' && !loadingUser && !videoFetched && !videoLoading) void loadVideos();
  }, [view, loadingUser, videoFetched, videoLoading, loadVideos]);
  useEffect(() => { if (!loadingUser) { setVideoFetched(false); setVideoPage(0); setVideoHasMore(true); } }, [isAuthenticated, loadingUser]);
  const selectPrimaryView = (mode: PrimaryCommunityView) => {
    setView(mode === 'videos' ? 'videos' : 'hot');
    try { window.localStorage.setItem(PRIMARY_VIEW_STORAGE_KEY, mode); }
    catch { /* Watch and browse still work without browser storage. */ }
    void router.replace({ pathname: '/community', query: { view: mode } },
      undefined, { shallow: true, scroll: false });
  };
  const feedTitle = useMemo(() => {
    if (view === 'videos') return 'Grocery videos';
    if (view === 'near') return 'Conversations near you';
    if (view === 'following') return 'Conversations you follow';
    if (view === 'spaces') return 'Find your people in Spaces';
    return 'Conversations worth joining';
  }, [view]);

  const toggleFollow = (postId: string) => {
    try {
      const next = toggleFollowedConversation(window.localStorage, postId);
      setFollowedIds(next);
    } catch {
      toast.error('Following could not be saved on this device.');
    }
  };

  const handleLike = async (postId: string) => {
    if (busyLikes[postId]) return;
    if (isAuthenticated && !userId) {
      toast.error('Your account is still loading. Please try again.');
      return;
    }
    setBusyLikes(old => ({ ...old, [postId]: true }));
    try {
      const result = isAuthenticated
        ? await toggleLike(postId, userId as string)
        : await toggleGuestLike(postId);
      setLiked(old => ({ ...old, [postId]: result.liked }));
      setPosts(old => old.map(post => post.post_id === postId
        ? { ...post, likes_count: result.likesCount ??
          Math.max(0, post.likes_count + (result.liked ? 1 : -1)) }
        : post));
      setVideoPosts(old => old.map(post => post.post_id === postId ? {
        ...post, likes_count: result.likesCount ?? Math.max(0,post.likes_count+(result.liked?1:-1))
      } : post));
      if (replyPost?.post_id === postId) {
        setReplyPost(old => old ? { ...old, likes_count: result.likesCount } : old);
      }
      toast.success(result.liked ? 'Liked!' : 'Like removed');
    } catch (error) {
      const rateLimited = error instanceof Error && error.message === 'rate_limit_exceeded';
      toast.error(rateLimited
        ? "You're liking posts quickly. Please wait and try again."
        : 'We could not update your like. Please try again.');
    } finally {
      setBusyLikes(old => ({ ...old, [postId]: false }));
    }
  };

  const sharePost = async (post: Post) => {
    const url = window.location.origin + '/community/post/' + encodeURIComponent(post.post_id);
    try {
      if (typeof navigator.share === 'function') {
        await navigator.share({ title: post.title || 'Youdle Community', url });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success('Link copied');
      }
    } catch (err) {
      if (!(err instanceof Error && err.name === 'AbortError'))
        toast.error('Unable to share this post.');
    }
  };

  const beginCompose = (categoryValue: string, question?: string) => {
    setVideoComposer(false);
    setInspirationQuestion(question ?? null);
    setComposerCategory(categoryValue);
    setComposerOpen(true);
  };

  const startVideoPost = () => {
    if (loadingUser) { toast.error('Checking your account. Please try again.'); return; }
    if (!isAuthenticated) {
      void router.push('/signin?redirect=' + encodeURIComponent(COMMUNITY_VIDEO_COMPOSE_PATH));
      return;
    }
    setTitle(''); setContent(''); setMedia([]); setInspirationQuestion(null);
    setComposerCategory('Reviews & Recommendations'); setVideoComposer(true); setComposerOpen(true);
  };

  const submitPost = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!content.trim() || (videoComposer && !media.some(file=>file.type==='video/mp4'))) return;
    if (isCommercialPost({title,content,fileNames:media.map(f=>f.name)})) {
      setComposerOpen(false);
      void router.push(FLYER_BUILDER_PATH);
      return;
    }
    if (!isAuthenticated) {
      try {
        window.sessionStorage.setItem('youdle-community-v5-draft', JSON.stringify({
          title, content, category: composerCategory, inspirationQuestion,
        }));
      } catch { /* Sign in remains available. */ }
      void router.push('/signin?redirect=' + encodeURIComponent('/community?compose=1'));
      return;
    }
    setSubmitting(true);
    try {
      const result = await createPost({
        title: title.trim() || undefined,
        content: content.trim(),
        category: composerCategory,
        ...(inspirationQuestion ? { inspiration_question: inspirationQuestion } : {}),
        flares: /^\d{5}$/.test(zip) ? [zip] : [],
        is_anonymous: anonymous,
      });
      let uploadFailed = false;
      for (const image of media) {
        try { await uploadPostMedia(result.post_id, image); }
        catch { uploadFailed = true; }
      }
      setContent(''); setTitle(''); setMedia([]); setAnonymous(false); setVideoComposer(false);
      setInspirationQuestion(null);
      if (inspirationQuestion) setInspirationRefresh(n => n + 1);
      setComposerOpen(false);
      if (videoComposer && !uploadFailed) { setVideoFetched(false); setVideoPage(0); setVideoHasMore(true); setView('videos'); }
      else { setView('hot'); setCategory('all'); setPage(1); setRefreshCount(n => n + 1); }
      toast.success(uploadFailed
        ? 'Post published, but an attachment could not be uploaded.'
        : 'Your post is published');
    } catch (err) {
      if (err instanceof Error && err.message.includes('business_promotion_requires_flyer')) {
        setComposerOpen(false);
        void router.push(FLYER_BUILDER_PATH);
      } else if (err instanceof Error && err.message.includes('idea_already_started')) {
        setInspirationRefresh(n => n + 1);
        toast.error('Someone else already started that idea. Choose another or publish your own conversation.');
      } else toast.error(err instanceof Error ? err.message : 'We could not publish your post.');
    } finally { setSubmitting(false); }
  };

  // After joining or signing in, resume the actual video upload, not just the feed.
  useEffect(() => {
    if (!router.isReady || router.query.compose !== 'video' || loadingUser) return;
    if (!isAuthenticated) {
      void router.replace('/signin?redirect=' + encodeURIComponent(COMMUNITY_VIDEO_COMPOSE_PATH));
      return;
    }
    setTitle(''); setContent(''); setMedia([]); setInspirationQuestion(null);
    setComposerCategory('Reviews & Recommendations');
    setView('videos');
    setVideoComposer(true);
    setComposerOpen(true);
    // Consume the action once so closing the dialog does not reopen it.
    void router.replace({ pathname: '/community', query: { view: 'videos' } },
      undefined, { shallow: true, scroll: false });
  }, [router.isReady, router.query.compose, loadingUser, isAuthenticated, router]);

  useEffect(() => {
    if (!router.isReady || router.query.compose === 'video') return;
    if (router.query.compose === '1') {
      setComposerOpen(true);
      // Native Youdle story pages pass a recognized editorial question into
      // this composer. Unrecognized query text cannot claim editorial ideas.
      const requestedQuestion = router.query.prompt;
      const requestedCategory = router.query.promptCategory;
      if (typeof requestedQuestion === 'string' &&
          isRecognizedInspirationQuestion(requestedQuestion)) {
        setTitle(requestedQuestion);
        setContent('');
        setInspirationQuestion(requestedQuestion);
        if (typeof requestedCategory === 'string' &&
            GROCERY_PROMPT_CATEGORIES.some(value => value === requestedCategory)) {
          setComposerCategory(requestedCategory);
        }
      }
    }
    try {
      const draft = window.sessionStorage.getItem('youdle-community-v5-draft');
      if (draft) {
        const value = JSON.parse(draft);
        setTitle(typeof value.title === 'string' ? value.title : '');
        setContent(typeof value.content === 'string' ? value.content : '');
        setInspirationQuestion(typeof value.inspirationQuestion === 'string' ? value.inspirationQuestion : null);
        setComposerCategory(typeof value.category === 'string' ? value.category : 'ISO Requests');
        setComposerOpen(true);
        window.sessionStorage.removeItem('youdle-community-v5-draft');
      }
    } catch { /* local draft unavailable */ }
  }, [router.isReady, router.query.compose, router.query.prompt, router.query.promptCategory]);

  const submitReply = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!replyPost || !reply.trim()) return;
    if (!isAuthenticated) {
      void router.push('/signin?redirect=' +
        encodeURIComponent('/community/post/' + replyPost.post_id));
      return;
    }
    if (!userId) return;
    setSubmittingReply(true);
    try {
      await addComment({ post_id: replyPost.post_id, user_id: userId,
        content: reply.trim(), parent_comment_id: null });
      setPosts(old => old.map(post => post.post_id === replyPost.post_id
        ? { ...post, comments_count: post.comments_count + 1 } : post));
      setReply('');
      setReplyPost(null);
      toast.success('Reply posted');
    } catch { toast.error('Could not post your reply.'); }
    finally { setSubmittingReply(false); }
  };

  const saveZip = (value: string) => {
    const clean = value.replace(/\D/g, '').slice(0, 5);
    setZip(clean);
    if (clean.length === 5) {
      try { window.localStorage.setItem(ZIP_STORAGE_KEY, clean); }
      catch { /* The ZIP can still be used for this visit. */ }
    }
  };

  return (
    <>
      <HomeHeader />
      <div className="youdle-v5">
      <Head>
        <title>Community | Youdle</title>
        <meta name="description" content="Talk groceries, trade tips, and help neighbors shop smarter in the Youdle Community." />
      </Head>
      <main className={'wrap main v5-social-layout' + (view === 'videos' ? ' v5-video-first' : '')}>
        <aside className="v5-social-left" aria-label="Explore grocery conversation topics">
          <div className="v5-social-rail-card">
            <h2>Browse topics</h2>
            <nav className="v5-social-topic-nav" aria-label="Grocery conversation topics">
              {categories.map(item => (
                <button key={item.id} type="button"
                  className={'v5-social-topic-button' + (view !== 'videos' && category === item.id ? ' active' : '')}
                  aria-pressed={view !== 'videos' && category === item.id}
                  onClick={() => { setCategory(item.id); selectPrimaryView('conversations'); }}>
                  {item.label}
                </button>
              ))}
            </nav>
          </div>
        </aside>
        <div className="v5-social-center">
          <header className="v5-social-heading">
            <div>
              <h1>Community</h1>
              <p>No one shops alone.</p>
            </div>
            {view === 'videos' ? (
              <button type="button" className="v5-social-video-action" onClick={startVideoPost}>＋ Post a video</button>
            ) : null}
          </header>
          <nav id="community-primary-view-tabs" className="v5-primary-view-tabs" aria-label="Choose how to explore Community">
            <button type="button" className={'v5-primary-view-tab' + (view === 'videos' ? ' active' : '')}
              aria-pressed={view === 'videos'}
              onClick={() => selectPrimaryView('videos')}>Videos</button>
            <button type="button" className={'v5-primary-view-tab' + (view !== 'videos' ? ' active' : '')}
              aria-pressed={view !== 'videos'}
              onClick={() => selectPrimaryView('conversations')}>Conversations</button>
          </nav>
          {view !== 'videos' && (
            <div className="v5-social-composer" aria-label="Start a Community post">
              <button type="button" className="v5-social-composer-prompt"
                onClick={() => beginCompose('ISO Requests')}>
                <span aria-hidden="true" className="v5-social-composer-icon">＋</span>
                <span>Ask a question or share a grocery find...</span>
              </button>
              <div className="v5-social-compose-actions">
                <button type="button" onClick={() => beginCompose('ISO Requests')}>Ask a question</button>
                <button type="button" onClick={() => beginCompose('Stock Updates')}>Share a Shelfie</button>
                <button type="button" onClick={startVideoPost}>Post a video</button>
              </div>
            </div>
          )}
        <section className="community-shell" aria-label="Youdle Community conversations">

          {view !== 'videos' && (
            <nav className="tabs v5-conversation-subtabs" aria-label="Filter conversations">
              {([
                { id: 'hot', name: 'Hot Today' },
                { id: 'near', name: 'Near Me' },
                { id: 'following', name: 'Following' },
              ] as const).map(tab => (
                <button key={tab.id} className={'tab ' + (view === tab.id ? 'active' : '')}
                  type="button" aria-current={view === tab.id ? 'page' : undefined}
                  onClick={() => setView(tab.id)}>{tab.name}</button>
              ))}
            </nav>
          )}

          
          {view !== 'spaces' && view !== 'videos' && (
            <div className="top-filters" id="feedTools">
              <div className="find-row">
                <label className="find-input">
                  <span className="sr-only">Search the Community</span>
                  <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <circle cx="10.6" cy="10.6" r="6.8" /><path d="m16 16 5 5" />
                  </svg>
                  <input type="search" autoComplete="off" value={search}
                    placeholder="Search Community conversations"
                    onChange={event => setSearch(event.target.value.slice(0, 150))} />
                </label>
                <label className="sort-label">Sort
                  <select className="youdle-select" value={sort} onChange={event => setSort(event.target.value as SortKey)}>
                    <option value="newest">Newest</option><option value="helpful">Helpful</option>
                  </select>
                </label>
              </div>
              <div className="chip-row" role="group" aria-label="Filter conversations">
                {categories.map(item => (
                  <button key={item.id} type="button" className={'chip ' + (category === item.id ? 'active' : '')}
                    aria-pressed={category === item.id} onClick={() => setCategory(item.id)}>{item.label}</button>
                ))}
              </div>
            </div>
          )}

          {view === 'near' && (
            <div className="flex flex-wrap items-center gap-3 border-b border-[#E0E7DE] bg-[#F8FAF7] px-5 py-3">
              <label htmlFor="community-nearby-zip" className="text-sm font-bold text-[#294936]">Shopping ZIP</label>
              <input id="community-nearby-zip" type="text" inputMode="numeric" autoComplete="postal-code"
                maxLength={5} value={zip} placeholder="Enter 5-digit ZIP"
                onChange={event => saveZip(event.target.value)}
                className="min-h-11 w-40 rounded-xl border border-[#C9D9CA] bg-white px-3 text-sm text-[#284632]" />
              <span className="text-xs text-[#66776C]">Used only to find nearby conversations</span>
            </div>
          )}

          {view === 'near' && /^\d{5}$/.test(zip) && (
            <div className="nearby-radius-row" role="group" aria-label="Distance from your ZIP code">
              <span>Show conversations within</span>
              <form className="nearby-radius-form" onSubmit={event => {
                event.preventDefault();
                const miles = Number(nearbyRadiusInput);
                if (!Number.isInteger(miles) || miles < 1 || miles > 50) return;
                setNearbyRadius(miles);
                try { window.localStorage.setItem('youdle-community-radius-miles', String(miles)); }
                catch { /* Optional device preference */ }
              }}>
                <input type="number" inputMode="numeric" min={1} max={50} step={1}
                  aria-label="Custom distance in miles" value={nearbyRadiusInput}
                  onChange={event => setNearbyRadiusInput(event.target.value)} required />
                <span>miles</span>
                <button type="submit" disabled={!/^(?:[1-9]|[1-4][0-9]|50)$/.test(nearbyRadiusInput)}>Apply</button>
              </form>
              <small>1–50 miles · Approximate ZIP-code distances</small>
            </div>
          )}
          <div className={'feed-head ' + (view === 'hot' ? 'with-topic-inspiration' : '')} id="feedHeader">
            <div className="feed-head-copy">
              <h2>{view === 'hot' ? 'All Conversations' : feedTitle}</h2>
              <span>{view === 'spaces'
                ? 'Pilot concepts, not active groups'
                : view === 'following'
                  ? 'Followed conversations are saved on this device'
                  : view === 'near'
                    ? (zip ? 'Within ' + nearbyRadius + ' miles of ZIP ' + zip : 'Choose your ZIP code')
                    : view === 'videos'
                      ? 'Tap any video to watch the entire clip · Scroll or swipe to keep watching'
                      : 'Real posts, questions, tips and grocery finds from shoppers'}
              </span>
            </div>
          </div>
          {view === 'hot' && (
            <GroceryTopicInspiration refreshKey={inspirationRefresh} onUseIdea={(question, postCategory) => {
              setTitle(question);
              setContent('');
              beginCompose(postCategory, question);
            }} />
          )}

          {view === 'videos' ? (
            <CommunityVideoFeed posts={videoPosts} loading={videoLoading} onCreateVideo={startVideoPost}
              hasMore={videoHasMore} loadError={videoLoadError}
              onRetry={() => void loadVideos(videoPage > 0)}
              liked={liked} busyLikes={busyLikes} followedIds={followedIds}
              onLike={id=>void handleLike(id)}
              onReply={post=>{setReplyPost(post);setReply('');}}
              onShare={post=>void sharePost(post)} onFollow={toggleFollow}
              onNearEnd={() => { if (!videoLoading && videoHasMore && !videoLoadError) void loadVideos(true); }} />
          ) : view === 'spaces' ? (
            <>
              <p className="v5-live-note" style={{ padding: '0 22px 12px' }}>
                Spaces are being prepared for moderated launch. These ideas are not live groups and do not have members yet.
              </p>
              <div className="space-items" id="spacesList">
                {spaceConcepts.map(space => (
                  <div className="space-card" key={space.name}>
                    <div className="space-icon" aria-hidden="true">{space.icon}</div>
                    <strong>{space.name}</strong>
                    <p>{space.summary}</p>
                    <div className="post-actions">
                      <button type="button" className="more-cta" onClick={() => {
                        setView('hot'); setSearch(space.search); setCategory('all');
                      }}>See related conversations →</button>
                    </div>
                    <small className="protection">{space.tag} · Not a live Space</small>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div id="postList" aria-live="polite" aria-busy={loading}>
              {view === 'near' && !/^\d{5}$/.test(zip) ? (
                <div className="no-results"><strong>See what neighbors are talking about</strong>
                  Enter your five-digit ZIP code above to see nearby grocery conversations.
                </div>
              ) : loading ? (
                <div className="v5-state">Loading grocery conversations...</div>
              ) : error ? (
                <div className="no-results"><strong>We could not load conversations</strong>
                  <div className="v5-error">{error}</div>
                  <button type="button" className="secondary" onClick={() => setRefreshCount(n => n + 1)}>Try again</button>
                </div>
              ) : posts.length === 0 ? (
                <div className="no-results"><strong>{view === 'following' ? 'No followed conversations yet' : 'No conversations found'}</strong>
                  {view === 'following'
                    ? 'Use Follow on a conversation to collect it here on this device.'
                    : 'Try another filter, or start a conversation for other shoppers.'}
                </div>
              ) : posts.map(post => {
                const author = getPostAuthorDisplay(post);
                const isFollowing = followedIds.includes(post.post_id);
                const isLiked = !!liked[post.post_id];
                const isExpanded = expandedPostId === post.post_id;
                const fold = getCommunityFoldPreview(post);
                const attachments = post.media?.slice(0, 4) ?? [];
                const videoCount = attachments.filter(item => item.media_type === 'video').length;
                const photoCount = attachments.length - videoCount;
                const previewPhoto = attachments.find(item => item.media_type !== 'video');
                const tag = videoCount > 0 && post.video_topic
                  ? { name: 'VIDEO · ' + post.video_topic.toUpperCase(), style: 'sky' }
                  : tagForCategory(post.category);
                const detailsId = 'v5-post-detail-' + post.post_id;
                return (
                  <article className={'post v5-foldable-post ' + (isExpanded ? 'v5-expanded' : 'v5-folded')}
                    key={post.post_id}>
                    <div className="post-meta">
                      <PublicAvatar src={author.avatarUrl} name={author.name} initials={author.initials.slice(0, 2)} size={35} />
                      <div className="user-info">
                        <strong>{author.name}</strong>
                        <small>{post.geo_zipcode ? post.geo_zipcode + ' · ' : ''}{postDate(post.created_at)}</small>
                      </div>
                    </div>
                    <div className="v5-post-labels">
                      <span className={'tag ' + tag.style + (videoCount > 0 ? ' v5-video-yellow' : '')}>{tag.name}</span>
                      {videoCount > 0 && <button type="button" className="v5-media-indicator v5-video-yellow v5-video-open"
                         aria-label={'Play ' + (videoCount === 1 ? 'video' : videoCount + ' videos') + ' from ' + (post.title || 'this Community conversation')}
                         onClick={() => setPlayingVideoPost(post)}>▶ {videoCount === 1 ? 'Video' : videoCount + ' videos'}</button>}
                      {photoCount > 0 && <span className="v5-media-indicator">▧ {photoCount === 1 ? 'Photo' : photoCount + ' photos'}</span>}
                    </div>
                    <button type="button" className="v5-fold-toggle"
                      aria-controls={detailsId}
                      aria-expanded={isExpanded}
                      onClick={() => setExpandedPostId(current =>
                        current === post.post_id ? null : post.post_id)}>
                      <span className="v5-fold-heading">{isExpanded ? fold.expandedHeading : fold.heading}</span>
                      <span className="v5-fold-action" aria-hidden="true">
                        {isExpanded ? 'Show less ⌃' : 'Read more ⌄'}
                      </span>
                    </button>
                    {!isExpanded && fold.excerpt && (
                      <p className="v5-fold-excerpt">{fold.excerpt}</p>
                    )}
                    {!isExpanded && previewPhoto && (
                      <button type="button" className="v5-social-photo-preview"
                        onClick={() => setExpandedPostId(post.post_id)}
                        aria-label="View photos and full conversation">
                        <img src={previewPhoto.media_url}
                          alt="Grocery find shared by a Community shopper" loading="lazy" />
                      </button>
                    )}
                    <div className="statusline">
                      <span>{post.likes_count} {post.likes_count === 1 ? 'like' : 'likes'}</span>
                      <span className="dot" /><span>{post.comments_count} {post.comments_count === 1 ? 'reply' : 'replies'}</span>
                    </div>
                    <div id={detailsId} hidden={!isExpanded} className="v5-post-expanded-content">
                      {isExpanded && (
                        <>
                          {fold.body && (
                            <p className="v5-full-content" style={{ whiteSpace: 'pre-wrap' }}>{fold.body}</p>
                          )}
                          {attachments.length > 0 && (
                            <div className="v5-image-media">
                              {attachments.map(mediaItem => mediaItem.media_type === 'video' ? (
                                <video key={mediaItem.media_id} controls playsInline preload="metadata"
                                  src={getVideoPreviewUrl(mediaItem.media_url)}
                                  aria-label="Community post video" />
                              ) : (
                                <a key={mediaItem.media_id} href={mediaItem.media_url}
                                  target="_blank" rel="noopener noreferrer"
                                  aria-label="Open grocery photo">
                                  <img src={mediaItem.media_url}
                                    alt="Photo shared with this Community conversation" loading="lazy" />
                                </a>
                              ))}
                            </div>
                          )}
                          <Link className="v5-open-thread" href={'/community/post/' + encodeURIComponent(post.post_id)}>
                            View full conversation and replies →
                          </Link>
                        </>
                      )}
                    </div>
                    <div className="post-actions v5-fold-actions">
                      <button type="button" className={isLiked ? 'active' : ''}
                        disabled={!!busyLikes[post.post_id]} aria-pressed={isLiked}
                        aria-label={isLiked ? 'Unlike conversation' : 'Like conversation'}
                        onClick={() => void handleLike(post.post_id)}>
                        {isLiked ? '♥' : '♡'} {isLiked ? 'Liked' : 'Like'}
                      </button>
                      <button type="button" onClick={() => { setReplyPost(post); setReply(''); }}>
                        ↪ Reply
                      </button>
                      <button type="button" onClick={() => void sharePost(post)}>↗ Share</button>
                      <button type="button" className={isFollowing ? 'active' : ''}
                        aria-pressed={isFollowing}
                        aria-label={isFollowing ? 'Unfollow conversation' : 'Follow conversation'}
                        onClick={() => toggleFollow(post.post_id)}>
                        {isFollowing ? '✓ Following' : '＋ Follow'}
                      </button>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
          <div className="bottom">
            <span id="feedCount">{view === 'videos'
              ? (videoTotal ? videoTotal + ' grocery videos' : 'Grocery video feed')
              : view === 'spaces'
                ? 'Spaces are not open yet'
                : total === 0 ? 'No matching conversations' : total + ' conversations'}</span>
            <div className="bottom-ctas">
              {view !== 'spaces' && view !== 'videos' && (
                <div className="v5-pagination">
                  <button type="button" disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}>← Previous</button>
                  <span>Page {page}</span>
                  <button type="button" disabled={page * PAGE_SIZE >= total}
                    onClick={() => setPage(p => p + 1)}>More ↓</button>
                </div>
              )}
            </div>
          </div>
        </section>
          <p className="footer">Real shopper posts, uploaded shelf photos, and store information are community contributions.
            Check prices and availability with stores before making a special trip.</p>
        </div>
        <aside className="v5-social-right" aria-label="About Youdle Community">
          <div className="v5-social-rail-card v5-social-context">
            <h2>What shoppers share</h2>
            <p>Grocery questions, real store finds, useful tips and local prices.</p>
            <div className="v5-social-context-divider" />
            <strong>Before you make a trip</strong>
            <p>Confirm prices and availability directly with the store.</p>
          </div>
        </aside>
      </main>

      <GoatChat isMember={isAuthenticated} onOpenChange={setGoatChatOpen} />
      <CommunityFlyerNudge zipcode={zip} suppressed={composerOpen || Boolean(replyPost) || goatChatOpen || Boolean(playingVideoPost)} />
      {playingVideoPost && <CommunityPostVideoViewer post={playingVideoPost} onClose={() => setPlayingVideoPost(null)} />}
      {composerOpen && (
        <div className="scrim open" role="presentation" onMouseDown={event => {
          if (event.target === event.currentTarget && !submitting) setComposerOpen(false);
        }}>
          <section className={"dialog " + (inspirationQuestion ? "idea-compose-dialog" : "")} role="dialog" aria-modal="true" aria-labelledby="v5-compose-title">
            <div className="dialog-head">
              <div><h2 id="v5-compose-title">{videoComposer ? 'Post a grocery video' : 'Start a grocery conversation'}</h2>
                <p>{videoComposer ? 'Upload an MP4 and add a caption about your grocery find, recipe or tip.' : 'Ask what you need or share a recent store find with neighbors.'}</p></div>
              <button className="close" type="button" disabled={submitting}
                onClick={() => setComposerOpen(false)} aria-label="Close">×</button>
            </div>
            <form className="dialog-body" onSubmit={event => void submitPost(event)}>
              {!isAuthenticated && (
                <div className="v5-soft-note">You can write now. Sign-in is required to publish a post. Your draft will be saved on this device during sign-in.</div>
              )}
              <label className="form-field">Conversation type
                <select className="youdle-select" value={composerCategory} onChange={event => setComposerCategory(event.target.value)}>
                  {createCategories.map(item => <option value={item.value} key={item.value}>{item.label}</option>)}
                </select>
              </label>
              {inspirationQuestion && (
                <div className="v5-soft-note">This suggestion will disappear only after your post is published.
                  <button type="button" onClick={() => setInspirationQuestion(null)}
                    style={{ marginLeft: 8, textDecoration: 'underline' }}>Use my own idea instead</button>
                </div>
              )}
              <label className="form-field">Title (optional)
                <input type="text" value={title} maxLength={200}
                  placeholder="What are you looking for?" onChange={event => setTitle(event.target.value)} />
              </label>
              <label className="form-field">Your message
                <textarea value={content} maxLength={10000} required
                  placeholder="Tell neighbors what you found or what you need, including the store and when you checked."
                  onChange={event => setContent(event.target.value)} />
              </label>
              <label className="form-field">{videoComposer ? 'Your grocery video (MP4)' : 'Add a Shelfie, store photo or video (up to four)'}
                <input ref={videoFileInputRef} type="file" accept={videoComposer ? "video/mp4" : "image/jpeg,image/png,image/webp,video/mp4,video/quicktime"}
                  multiple={!videoComposer} onChange={event => {
                    const selected=Array.from(event.target.files ?? []).slice(0,videoComposer ? 1 : MAX_MEDIA);
                    if (selected.some(file=>file.size>50*1024*1024)) { toast.error("Videos and photos must be under 50 MB."); event.target.value=""; return; }
                    if(videoComposer && selected.some(file=>file.type!=="video/mp4")) { toast.error("Choose an MP4 video."); event.target.value="";return; }
                    setMedia(selected);
                  }} />
                {media.length > 0 && <small>{media.map(f => f.name).join(', ')}</small>}
              </label>
              {isAuthenticated && (
                <label style={{ display: 'flex', gap: 8, fontSize: 13 }}>
                  <input type="checkbox" checked={anonymous} onChange={event => setAnonymous(event.target.checked)} />
                  Post without showing my name
                </label>
              )}
              <div className="form-actions">
                <button className="primary red" disabled={submitting || !content.trim() || (videoComposer && !media.some(f=>f.type==="video/mp4"))} type="submit">
                  {submitting ? 'Publishing...' : isAuthenticated ? (videoComposer ? 'Publish video' : 'Publish conversation') : 'Sign in to publish'}
                </button>
                <button className="secondary" type="button" disabled={submitting} onClick={() => setComposerOpen(false)}>Cancel</button>
              </div>
              <p className="form-note">Never post home addresses or personal details. Business promotions belong in <Link href={FLYER_BUILDER_PATH}>Create Your Flyer</Link>, not the Community feed.</p>
            </form>
          </section>
        </div>
      )}

      {replyPost && (
        <div className="scrim open" role="presentation" onMouseDown={event => {
          if (event.target === event.currentTarget) setReplyPost(null);
        }}>
          <section className="dialog" role="dialog" aria-modal="true" aria-labelledby="v5-reply-title">
            <div className="dialog-head">
              <div><h2 id="v5-reply-title">Join the conversation</h2>
                <p>{replyPost.title || replyPost.content.slice(0, 110)}</p></div>
              <button className="close" type="button" onClick={() => setReplyPost(null)} aria-label="Close">×</button>
            </div>
            <form className="dialog-body" onSubmit={event => void submitReply(event)}>
              <label className="form-field">Your reply
                <textarea value={reply} maxLength={10000} onChange={event => setReply(event.target.value)}
                  required placeholder="Share a specific store, product size, price, or helpful grocery tip." />
              </label>
              {!isAuthenticated && <p className="form-note">You can like without joining. A Youdle account is required to reply.</p>}
              <div className="form-actions">
                <button className="primary red" type="submit" disabled={!reply.trim() || submittingReply}>
                  {submittingReply ? 'Posting...' : isAuthenticated ? 'Post reply' : 'Sign in to reply'}
                </button>
                <Link className="secondary" href={'/community/post/' + encodeURIComponent(replyPost.post_id)}>
                  See all replies
                </Link>
              </div>
            </form>
          </section>
        </div>
      )}
      </div>
    </>
  );
}
